/**
 * Token Usage Service
 *
 * API service for Pipedrive token tracking endpoints.
 * Connects to FastAPI /api/token-usage endpoints.
 */

import { apiClient } from './apiClient';
import { apiCall } from '@shared/utils/apiWrapper';

/**
 * Token usage status response from API
 */
export interface TokenUsageStatus {
  /** Current tokens used in this period */
  used: number;
  /** Total token budget for this period */
  budget: number;
  /** Remaining tokens (budget - used) */
  remaining: number;
  /** Percentage used (0-100) */
  percentage: number;
  /** Status level based on percentage thresholds */
  status: 'NORMAL' | 'CAUTION' | 'WARNING' | 'CRITICAL';
  /** Human-readable time until reset (e.g., "3h 45m") */
  time_until_reset: string;
  /** ISO timestamp of next reset */
  reset_time: string;
  /** Actual remaining tokens from Pipedrive API (null if not synced) */
  api_actual_remaining: number | null;
  /** Percentage accuracy of estimation (0-100) */
  estimation_accuracy: number;
}

/**
 * Usage breakdown by endpoint
 */
export interface UsageBreakdown {
  /** Endpoint-level token usage */
  breakdown: Record<string, number>;
  /** Total tokens used across all endpoints */
  total: number;
  /** Time period in hours */
  hours: number;
}

/**
 * Token usage service methods
 */
export const tokenUsageService = {
  /**
   * Get current token usage status
   *
   * @returns Promise resolving to token usage status
   *
   * @example
   * ```typescript
   * const status = await tokenUsageService.getStatus();
   * console.log(`Used: ${status.used}/${status.budget} (${status.percentage}%)`);
   * console.log(`Status: ${status.status}`);
   * console.log(`Resets in: ${status.time_until_reset}`);
   * ```
   */
  getStatus: async (): Promise<TokenUsageStatus> => {
    return apiCall('fetch token usage status', () =>
      apiClient.get<TokenUsageStatus>('/token-usage/status')
    );
  },

  /**
   * Get usage breakdown by endpoint
   *
   * @param hours - Number of hours to look back (default: 24)
   * @returns Promise resolving to usage breakdown
   *
   * @example
   * ```typescript
   * const breakdown = await tokenUsageService.getBreakdown(24);
   * console.log(`Total used: ${breakdown.total} tokens`);
   * Object.entries(breakdown.breakdown).forEach(([endpoint, tokens]) => {
   *   console.log(`  ${endpoint}: ${tokens} tokens`);
   * });
   * ```
   */
  getBreakdown: async (hours: number = 24): Promise<UsageBreakdown> => {
    return apiCall(`fetch token usage breakdown (${hours}h)`, () =>
      apiClient.get<UsageBreakdown>('/token-usage/breakdown', { params: { hours } })
    );
  },
};
